/*
 *
 *  * GEHC CONFIDENTIAL
 *  *
 *  * Copyright (c) 2020, 2025, GE HealthCare
 *  * All Rights Reserved
 *  *
 *  * This unpublished material is proprietary to GE HealthCare. The methods and
 *  * techniques described herein are considered trade secrets and/or
 *  * confidential. Reproduction or distribution, in whole or in part, is
 *  * forbidden except by express written permission of GE HealthCare.
 *  * GE is a trademark of General Electric Company used under trademark license.
 *
 */

import * as _ from 'underscore';
import { Pipe, PipeTransform } from '@angular/core';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { AppConfigService } from '@app/common/app-config.service';
import { ArchiveAdoptedService } from '@app/common/services/bring-online/archive-adopted.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { appConstants } from '@app/common/app-constants';
import { UtilService } from '@app/util/util.service';
import { TranslateService } from '@ngx-translate/core';
import { RadNamePipe } from '@app/common/pipes/rad-name.pipe';

@Pipe({
  name: 'workItem'
})
export class WorkItemPipe implements PipeTransform {
  constructor(
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private archiveAdoptedService: ArchiveAdoptedService,
    private userPreferenceService: UserPreferenceService,
    private integrationStatus: IntegrationStatusService,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private utilService: UtilService,
    private radNamePipe: RadNamePipe
  ) {}

  transform(value: any, inputType: string, data?: any): any {
    let pipeResult: any = undefined;
    switch (inputType) {
      case 'state-tooltip-disable': {
        pipeResult = !(
          !!value &&
          (this.utilService.isLocked(value) ||
            this.utilService.isReading(value) ||
            this.utilService.wfmOrApproverAssignment(value) ||
            this.utilService.isExternallyOpened(value))
        );
        break;
      }
      case 'isWfmAssigned': {
        pipeResult = this.utilService.wfmOrApproverAssignment(value);
        break;
      }
      case 'isWfmEdit': {
        pipeResult = this.utilService.wfmOrApproverEdit(value);
        break;
      }
      case 'sort-user-list': {
        pipeResult = false;
        if (value && value.ownership) {
          const obj = _.filter(value.ownership.assignedToUser, obj => {
            return obj.extStatusFlag !== 9 && obj.extStatusFlag !== 1 && data && data.indexOf(obj.extStatusFlag) > -1;
          });
          if (obj.length > 0) {
            const sortedObj = obj.sort((a, b) => {
              const textA = a.lastName.toUpperCase();
              const textB = b.lastName.toUpperCase();
              return textA < textB ? -1 : textA > textB ? 1 : 0;
            });
            pipeResult = sortedObj;
          }
        }
        break;
      }
      case 'state-icon': {
        pipeResult = '';
        if (value && value.ownership && value.activity) {
          if (data) {
            if (value.isAssignedToSelf) {
              pipeResult = appConstants.stateIcons.locked;
            } else if (value.isAssignedToOther) {
              pipeResult = appConstants.stateIcons.locked;
            } else if (value.extAppWorkItemState === 'External_Read_Lock_Added') {
              pipeResult = appConstants.stateIcons.monitor;
            }
          } else if (value?.activity?.editing) {
            pipeResult = appConstants.stateIcons.locked;
          } else {
            if (value.isActivatedBySelf) {
              pipeResult = appConstants.stateIcons.reading;
            } else if (value.isActivatedByOther) {
              pipeResult = appConstants.stateIcons.reading;
            } else if (value.isAssignedToSelf) {
              pipeResult = appConstants.stateIcons.locked;
            } else if (value.isAssignedToOther) {
              pipeResult = appConstants.stateIcons.locked;
            } else if (value.extAppWorkItemState === 'External_Read_Lock_Added') {
              pipeResult = appConstants.stateIcons.monitor;
            } else if (value && value.ownership && value.activity) {
              pipeResult = appConstants.stateIcons.unlocked;
            }
          }
        }
        break;
      }
      case 'state-class': {
        pipeResult = '';
        if (value?.activity?.editing) {
          if (value.currentUser === value.ownerName) {
            pipeResult = appConstants.stateClasses.self;
          } else {
            pipeResult = appConstants.stateClasses.other;
          }
        } else if (data) {
          if (value.isAssignedToSelf) {
            pipeResult = appConstants.stateClasses.self;
          } else if (value.isAssignedToOther) {
            pipeResult = appConstants.stateClasses.other;
          } else if (value.extAppWorkItemState === 'External_Read_Lock_Added') {
            pipeResult = appConstants.stateClasses.external;
          }
        } else if (value && value.ownership && value.activity) {
          if (value.isActivatedBySelf) {
            pipeResult = appConstants.stateClasses.self;
          } else if (value.isActivatedByOther) {
            pipeResult = appConstants.stateClasses.other;
          } else if (value.isSmsOperationInProgress) {
            pipeResult = appConstants.stateClasses.other;
          } else if (value.isAssignedToSelf) {
            pipeResult = appConstants.stateClasses.self;
          } else if (value.isAssignedToOther) {
            pipeResult = appConstants.stateClasses.other;
          } else if (value.extAppWorkItemState === 'External_Read_Lock_Added') {
            pipeResult = appConstants.stateClasses.external;
          } else if (value && value.ownership && value.activity) {
            pipeResult = appConstants.stateClasses.none;
          }
        }
        break;
      }
      case 'instituteName': {
        const instituteList = this.searchFilterCriteriaService.getInstitutes();
        const institute = _.find(instituteList, o => {
          return o.instituteId === value;
        });
        pipeResult = !!institute ? institute.instituteName : '';
        break;
      }
      case 'reportStatus': {
        const reportStatusDescList = this.searchFilterCriteriaService.getReportStatuses();
        const reportStatus = _.find(reportStatusDescList, o => {
          return o.reportStatusId === value;
        });
        pipeResult = reportStatus ? reportStatus.reportStatusDescription : '';
        break;
      }
      case 'aiFinding': {
        pipeResult =
          value > 0 &&
          this.userPreferenceService.getUserPreferenceFromCache('show-ai-findings') === true &&
          this.appConfigService.getAppConfigByKey('ai.findings.enabled') === 'ON';
        break;
      }
      case 'protocol-icon': {
        pipeResult = undefined;
        if (value !== null) {
          if (value === 10) {
            pipeResult = 'ico-checkmark-16';
          } else if (value === 20) {
            pipeResult = 'ico-protocol-needs-reviewed-16';
          } else if (value === 40) {
            pipeResult = 'ico-checkmark-circle-16';
          }
        }
        break;
      }
      case 'archive-icon-tooltip': {
        pipeResult = this.archiveAdoptedService.getArchiveIconTooltip(data);
        break;
      }
      case 'archive-icon': {
        pipeResult = this.archiveAdoptedService.getArchiveIcon(data);
        break;
      }
      case 'remote-icon': {
        pipeResult = this.archiveAdoptedService.getArchiveFetchStatus(value.id, UtilService.fetchStudyDataParam(value));
        break;
      }
      case 'tab-archive-icon': {
        if (data) {
          pipeResult = data.workItem.keys.archiveFetchStatus === appConstants.archiveAdoptionStatus.InProgress;
        }
        break;
      }
      case 'exceptions': {
        pipeResult =
          value && ((value.exceptions && (parseInt(value.exceptions, 10) & 7) > 0) || value.reportStatusId % 10 === 5);
        break;
      }
      case 'exceptionDisplay': {
        const result = [];
        /*jslint bitwise: true */
        if ((parseInt(value.exceptions, 10) & parseInt('1', 10)) > 0) {
          result.push(this.translate.instant('exception.newContent'));
        }
        /*jslint bitwise: true */
        if ((parseInt(value.exceptions, 10) & 2) > 0) {
          result.push(this.translate.instant('exception.noOrder'));
        }
        /*jslint bitwise: true */
        if ((parseInt(value.exceptions, 10) & 4) > 0) {
          result.push(this.translate.instant('exception.patientMismatch'));
        }
        // Add the temporary exception
        if (value.reportStatusId % 10 === 5) {
          result.push(this.translate.instant('exception.temporaryException'));
        }
        pipeResult = result.join(data === 'tooltip' ? '<br>' : ', ');
        break;
      }
      case 'folderMemberInfo': {
        pipeResult =
          this.translate.instant('workloadManager.listContainer.folderMemberMsg') +
          '  ' +
          this.radNamePipe.transform(value.owner) +
          this.translate.instant('workloadManager.listContainer.creatorText') +
          '; ';

        if (value.sharees instanceof Array) {
          value.sharees.forEach(sharee => {
            pipeResult += sharee.userName;
            pipeResult += '; ';
          });
          pipeResult = pipeResult.substring(0, pipeResult.length - 2);
        }
        break;
      }
      case 'displayName': {
        const auth = this.searchFilterCriteriaService.getAuthor(value);
        pipeResult = !!auth ? auth['name'] : '';
        break;
      }
      case 'displayRefPhyName': {
        let name = '';
        const refPhy = this.searchFilterCriteriaService.getAuthor(value.practitionerId);
        if (!!refPhy && refPhy['name'] !== 'Unspecified') {
          name = refPhy['name'];
        } else {
          name = value.name?.text;
        }

        pipeResult = name;
        break;
      }
      case 'isGlobalExam': {
        pipeResult = UtilService.isGlobalExam(value);
        break;
      }
      case 'isRemoteExam': {
        pipeResult = UtilService.isRemoteExam(value);
        break;
      }
    }

    return pipeResult;
  }
}
